// Header.js — Cabeçalho principal da loja.
// Estrutura em 3 níveis:
//   1) Faixa superior laranja: telefone + FAQ + AJUDA & SUPORTE | redes sociais
//   2) Faixa principal navy: logo + Minha lista/Carrinho/Créditos/E-books/
//      Tema/Saldos/Perfil
//   3) Faixa inferior: TODAS AS CATEGORIAS + atalhos + Publique
//
// ================= POR QUE NÃO HÁ BUSCA AQUI (11/09/2026) =================
//
// Havia um campo de busca no meio desta barra, com um índice de ~70 KB
// (/ebookNameAndId) baixado no primeiro foco só para decidir se o termo era
// livro ou autor e então navegar para /livros ou /autores.
//
// O dono mandou tirá-lo, e a razão é que ele virou a terceira cópia da mesma
// busca: /livros e /autores ganharam cada um a SUA barra, presa ao painel de
// categorias e à paginação daquela página. O campo do cabeçalho fazia o mesmo
// trabalho pior — sem filtro, sem contagem de resultados — e ainda decidia
// sozinho, pelo texto digitado, em qual das duas páginas a pessoa ia parar.
//
// Consequência para quem for mexer aqui: o `?busca=` na URL continua valendo,
// porque quem lê o parâmetro são as próprias páginas. O que sumiu foi só o
// campo — e, com ele, `semAcento`, o índice e o `updateURL` que moravam neste
// arquivo. A rota /ebookNameAndId segue de pé no servidor; ninguém na loja a
// consome mais.
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MenuBars from "../../assets/imgs/menu-bars.svg";
import Menu from "./Menu";
import NavRotas from "./NavRotas";
import CategoriasDropdown from "./CategoriasDropdown";
import Carrinho from "../carrinho/Carrinho";
import { useAuth } from "../../services/context/AuthContext";
import useProfileData from "../../hooks/userProfileData";
import SaldoCreditos from "../perfil/creditos/SaldoCreditos";
import celllogo from "../../assets/imgs/logo (1).png";
import logo from "../../assets/imgs/logo.png";
import { CarrinhoContext } from "../../services/context/CarrinhoContext";
import { getTheme, toggleTheme } from "../../theme";
import useNavbarOculta from "../../hooks/useNavbarOculta";
import MarcaPadrao from "../ui/MarcaPadrao";
import NossosSites from "../ui/NossosSites";
// Telefone, WhatsApp e o link de "publique" saem de um lugar só. O número da
// faixa laranja era literal AQUI e ficou um dígito menor que o WhatsApp do
// CONTATO, três blocos abaixo no mesmo arquivo: duas escritas do mesmo dado,
// nenhuma ligação entre elas, e o erro sobreviveu à vida inteira do componente.
import {
  TELEFONE_TEXTO,
  TELEFONE_HREF,
  WHATSAPP_HREF,
  WHATSAPP_PUBLICAR_HREF,
} from "../../config/contato";

const Header = () => {
  const { totalItens } = useContext(CarrinhoContext);
  const main = process.env.REACT_APP_MAIN;
  const API_URL = process.env.REACT_APP_API;
  const [isOpen, setIsOpen] = useState(false);
  const [isCarrinhoOpen, setCarrinhoIsOpen] = useState(false);
  // Painel aberto: "categorias" | null
  // Havia também "promocoes", que saiu junto com a Promoção da Semana: o
  // preço promocional só existia no CMS, e o checkout recusava o pedido
  // por divergir de `ebooks.preco`. O desconto agora nasce no servidor.
  const [openDropdown, setOpenDropdown] = useState(null);
  const { user } = useAuth();
  const { profileData } = useProfileData();

  // O cabeçalho sai do caminho ao rolar para baixo e volta ao subir.
  //
  // Ele fica travado no lugar enquanto o menu lateral, o carrinho ou um dos
  // painéis está aberto: esconder o cabeçalho enquanto a pessoa mexe num
  // menu que sai DELE é fazer o menu flutuar preso a nada.
  useNavbarOculta(!isOpen && !isCarrinhoOpen && !openDropdown);

  // Tema claro/escuro (ícone do botão acompanha o tema atual)
  const [theme, setTheme] = useState(getTheme());
  useEffect(() => {
    const onChange = (e) => setTheme(e.detail);
    window.addEventListener("editora-theme-change", onChange);
    return () => window.removeEventListener("editora-theme-change", onChange);
  }, []);

  // Foto + nome do usuário logado (para o botão de Perfil)
  const rawFoto = profileData?.image;
  const fotoPerfil = rawFoto
    ? (/^(https?:|data:|blob:)/i.test(rawFoto) ? rawFoto : `${API_URL}public/${rawFoto}`)
    : null;
  const primeiroNome = (profileData?.nome || "").trim().split(" ")[0] || "Perfil";
  // Os dois selos do cabeçalho. "admin" entra em `ehAutor` pelo mesmo motivo do
  // perfil: quem administra a editora também publica.
  const ehAutor = ["autor", "admin"].includes(profileData?.permissao);
  const [clubeAtivo, setClubeAtivo] = useState(false);

  // A altura da faixa laranja vira `--faixa-h`, que o CSS usa para subir o
  // cabeçalho exatamente o tanto dela quando a página sai do topo. Abaixo de
  // 640px ela é `hidden`, o que mede zero — e zero é justamente o certo ali.
  const faixaRef = useRef(null);
  useEffect(() => {
    const el = faixaRef.current;
    if (!el) return undefined;
    const publicar = () =>
      document.documentElement.style.setProperty("--faixa-h", el.offsetHeight + "px");
    publicar();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(publicar);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const location = useLocation();
  const navigate = useNavigate();

  // Trocar de rota fecha o painel de categorias. Ele é ancorado no cabeçalho,
  // que sobrevive à navegação: sem isto, o painel continuaria aberto por cima
  // da página nova.
  useEffect(() => {
    setOpenDropdown(null);
  }, [location.pathname, location.search]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const toggleCarrinho = () => {
    document.body.classList.toggle("menu-open", !isCarrinhoOpen);
    setCarrinhoIsOpen(!isCarrinhoOpen);
  };
  const toggleSidebar = () => setIsOpen(!isOpen);

  // Minha lista, Créditos, E-books e o "+" dos saldos levam a ABAS do perfil,
  // e o perfil não põe a aba na URL: navega-se para a rota e o evento
  // `changePerfiltab` diz qual aba abrir. Os 150ms dão tempo de o Perfil montar
  // e registrar o ouvinte quando a pessoa vem de outra página. Eram quatro
  // cópias desta sequência no arquivo, uma por botão.
  const abrirAbaDoPerfil = (aba) => {
    if (desviarParaLogin(main + "perfil", aba)) return;
    scrollToTop();
    navigate(main + "perfil");
    setTimeout(() => window.dispatchEvent(new CustomEvent("changePerfiltab", { detail: aba })), 150);
  };

  /**
   * Manda quem não tem conta para o login, e devolve `true` quando desviou.
   *
   * Tudo o que há atrás destes botões é pessoal: os e-books que a pessoa tem, a
   * lista dela, o carrinho dela, o saldo dela. Sem conta não existe nada para
   * mostrar — e o perfil ainda montava a tela antes de se dar conta disso,
   * piscando uma página vazia antes de jogar para o login. O dono pediu o
   * desvio direto (15/09/2026).
   *
   * O DESTINO VIAJA JUNTO. `state.de` é a rota e `state.aba` é a aba do perfil:
   * quem clicou em "Minha lista" volta na Minha lista depois de entrar, e não
   * no perfil genérico. Mandar a pessoa para o login já é um pedido a mais; fazê-la
   * procurar de novo onde ela estava indo seria o segundo.
   */
  const desviarParaLogin = (destino, aba) => {
    if (user) return false;
    scrollToTop();
    navigate(main + "login", { state: { de: destino, aba } });
    return true;
  };

  // Links da barra inferior (categorias e atalhos).
  // `external` → abre em nova aba via <a> (ex.: Contato no WhatsApp).
  const navLinks = [
    // `exact`: a home é prefixo de TODAS as outras rotas, então sem isso o
    // marcador acharia que estamos no início em qualquer página do site.
    { label: "INÍCIO", to: main, exact: true },
    { label: "LIVROS", to: main + "livros" },
    { label: "AUTORES", to: main + "autores" },
    // Depois de LIVROS e AUTORES: quem já se interessou pelo acervo é quem tem
    // motivo para assinar. Antes deles, seria vender assinatura para quem ainda
    // não sabe o que a editora publica.
    { label: "CLUBE", to: main + "clube" },
    // CRÉDITOS morou aqui, ao lado do CLUBE, até subir para a faixa de cima
    // junto do carrinho (pedido do dono, 11/09/2026): ela leva a uma aba do
    // perfil, como Minha lista, e não a uma página do acervo como as vizinhas.
    { label: "FAQ", to: main + "faq" },
    { label: "CONTATO", to: WHATSAPP_HREF, external: true },
  ];

  return (
    <header className="brand-dark w-full fixed top-0 left-0 z-[101] shadow-[0_1px_1px_rgba(0,0,0,0.5)]">
      {/* ===== Faixa superior laranja =====
          Ela só aparece no alto da página: fora do topo, o cabeçalho sobe
          exatamente a altura dela (ver --faixa-h e `pagina-no-topo` no
          App.css). A altura é MEDIDA abaixo, não chutada. */}
      <div
        ref={faixaRef}
        className="bg-insigne-orange text-ink-fixed hidden sm:flex items-center justify-between px-6 md:px-12 py-2 text-sm">
        {/* Esquerda: telefone, FAQ, ajuda */}
        <div className="flex items-center gap-3 md:gap-5">
          {/* Era aqui que faltava o 9. O número agora é derivado do E.164 de
              config/contato.js — não há mais como esta linha divergir do link
              do WhatsApp sem que as duas mudem juntas.
              Virou `tel:` porque a faixa é `hidden sm:flex`: ela só existe de
              640px para cima, o que inclui tablet e celular deitado, onde tocar
              o número é o gesto natural. Consequência para quem for conferir a
              correção: no celular em pé a faixa inteira não é renderizada, então
              testar só no telefone dá falso negativo — o número "sumiu" porque
              a barra não existe naquela largura, não porque o ajuste falhou. */}
          <a
            href={TELEFONE_HREF}
            className="flex items-center gap-2 !text-ink-fixed"
          >
            <i className="bi bi-headset" />
            {TELEFONE_TEXTO}
          </a>
          <span className="hidden md:inline opacity-50">|</span>
          <Link to={main + "#faq"} className="!text-ink-fixed">
            FAQ
          </Link>
          <span className="hidden md:inline opacity-50">|</span>
          <Link
            to={main + "contato"}
            className="!text-ink-fixed underline font-semibold"
          >
            AJUDA &amp; SUPORTE
          </Link>
        </div>
        {/* Direita: os outros sites da Insigne e as redes sociais. Os sites só
            de 1024px para cima: abaixo disso a faixa não tem largura, e a
            gaveta do menu leva aos mesmos lugares. */}
        <div className="flex items-center gap-3">
          <NossosSites className="hidden lg:flex" />
          <span className="hidden lg:inline opacity-50 mx-2">|</span>
          <Link
            to="https://www.linkedin.com/company/insigneacad%C3%AAmica/"
            target="_blank"
            className="!text-ink-fixed"
          >
            <i className="bi bi-linkedin text-base" />
          </Link>
          <Link
            to="https://www.facebook.com/insigneacademica"
            target="_blank"
            className="!text-ink-fixed"
          >
            <i className="bi bi-facebook text-base" />
          </Link>
          <Link
            to="https://www.instagram.com/insigneacademica"
            target="_blank"
            className="!text-ink-fixed"
          >
            <i className="bi bi-instagram text-base" />
          </Link>
        </div>
      </div>

      {/* ===== Faixa principal: logo + ações ===== */}
      <div className="bg-insigne-navy w-full">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4 md:gap-6">
          {/* Logo */}
          <Link to={main} onClick={scrollToTop} className="shrink-0">
            <img
              src={logo}
              className="hidden md:block h-[60px] w-auto"
              alt="Insigne Acadêmica"
            />
            <img
              src={celllogo}
              className="block md:hidden h-[48px] w-auto"
              alt="Insigne Acadêmica"
            />
          </Link>

          {/* Ações: e-books | minha lista | carrinho | créditos | tema | saldos | perfil */}
          <div className="flex items-center gap-4 md:gap-6 text-white text-xs md:text-sm">
            {/* ===== A ORDEM DA FILEIRA (11/09/2026, o dono deixou a escolha) =====
                Dois grupos, lidos da esquerda para a direita:
                  SEUS LIVROS: E-books (o que você tem) e Minha lista (o que
                  você quer ter);
                  COMPRA: Carrinho e Créditos.
                CRÉDITOS FECHA A FILEIRA, colado na linha divisória, porque do
                outro lado dela moram os saldos que ele recarrega — o "+" dos
                saldos abre a mesma aba. E-books abre porque é a porta que quem
                já comprou mais usa: é por ela que se volta a ler.

                E-books e Créditos são portas para ABAS do perfil, como Minha
                lista, e por isso moram aqui e não entre LIVROS e AUTORES, que
                são páginas. Os ícones são os mesmos da barra lateral do
                perfil. O RÓTULO dos dois só aparece a partir de 1280px: entre
                640 e 1280 a fileira já leva carrinho, tema, saldos e foto com
                nome, e dois rótulos a mais empurrariam a foto para fora. Abaixo
                disso fica o ícone, com `title` e `aria-label`. No celular
                (<640px) os dois somem, como a Minha lista: não cabem ao lado
                do logo. */}
            <button
              onClick={() => abrirAbaDoPerfil("meus-livros")}
              title="Meus e-books"
              aria-label="E-books"
              className="hidden sm:flex items-center gap-2 hover:text-insigne-orange transition-colors"
            >
              <i className="bi bi-collection-play text-xl" />
              <span className="hidden xl:inline uppercase">E-books</span>
            </button>

            {/* Minha lista (apenas desktop) — abre a aba "Minha Lista" no perfil */}
            <button
              onClick={() => abrirAbaDoPerfil("minha-lista")}
              className="hidden lg:flex items-center gap-2 hover:text-insigne-orange transition-colors"
            >
              <i className="bi bi-heart text-xl" />
              <span className="uppercase">Minha lista</span>
            </button>

            {/* Carrinho — abre a página /carrinho */}
            <button
              onClick={() => {
                if (desviarParaLogin(main + "carrinho")) return;
                scrollToTop();
                navigate(main + "carrinho");
              }}
              className="relative flex items-center gap-2 hover:text-insigne-orange transition-colors"
            >
              <i className="uil uil-shopping-cart text-2xl" />
              <span className="hidden sm:inline uppercase">Carrinho</span>
              {totalItens > 0 && (
                <span className="absolute -top-2 -left-1 w-5 h-5 rounded-full bg-insigne-orange text-paper text-xs flex items-center justify-center font-bold">
                  {totalItens}
                </span>
              )}
            </button>

            <button
              onClick={() => abrirAbaDoPerfil("creditos")}
              title="Loja de Créditos"
              aria-label="Créditos"
              className="hidden sm:flex items-center gap-2 hover:text-insigne-orange transition-colors"
            >
              <i className="bi bi-bag-plus text-xl" />
              <span className="hidden xl:inline uppercase">Créditos</span>
            </button>

            {/* ===== CLARO/ESCURO, DEPOIS DAS AÇÕES DA LOJA (11/09/2026) =====
                Ele abria a fileira, na ponta esquerda, onde a primeira coisa
                que a pessoa encontrava ao varrer as ações era uma preferência
                de exibição — antes da lista e do carrinho, que são o que ela
                veio fazer. O dono pediu depois do carrinho, e é o lugar certo:
                comprar primeiro, ajustar a tela depois. Créditos e E-books
                entraram entre os dois depois, e ele seguiu sendo o último.

                Fica ANTES da linha divisória de propósito. À direita dela só
                mora o que é da conta (saldo, perfil), e o tema vale para a
                visita inteira, logada ou não. */}
            <button
              onClick={() => setTheme(toggleTheme())}
              title={theme === "light" ? "Ativar tema escuro" : "Ativar tema claro"}
              aria-label="Alternar tema"
              className="flex items-center hover:text-insigne-orange transition-colors"
            >
              <i className={"bi text-xl " + (theme === "light" ? "bi-moon-stars-fill" : "bi-brightness-high-fill")} />
            </button>

            {/* ===== OS SALDOS, À ESQUERDA DA FOTO (pedido do dono, 11/09/2026) =====
                Ticket em cima, Coin embaixo, e o "+" à direita dos dois — o
                desenho que ele mandou. Só a partir de `sm`: num celular de
                360px, o cabeçalho já disputa espaço entre logo, carrinho e
                perfil, e dois números a mais empurrariam o avatar para fora.
                Lá os saldos continuam no cabeçalho do perfil, onde nasceram.

                É a MESMA requisição do perfil, com cache de 60s compartilhado
                (ver SaldoCreditos): montar os dois cabeçalhos não custa duas
                idas ao servidor. */}
            {user && (
              <>
              {/* A LINHA QUE SEPARA COMPRA DE SALDO (pedido do dono,
                  11/09/2026). A esquerda dela ficam as acoes da loja (lista,
                  carrinho, creditos, e-books, tema); a direita, o que e da
                  conta (saldo, perfil). Sem o
                  traco, o "48" de Tickets encostava no carrinho e parecia mais
                  um contador dele.

                  As margens sao IGUAIS dos dois lados e se somam ao `gap` da
                  fileira, entao a linha cai no meio do vao — e ela so existe
                  quando os saldos existem (logado, e a partir de 640px). */}
              <span
                aria-hidden="true"
                className="hidden sm:block h-9 w-px shrink-0 bg-white/20 mx-1.5 md:mx-2.5"
              />
              <SaldoCreditos
                layout="coluna"
                className="hidden sm:flex"
                // A MESMA resposta do saldo traz o estado do Clube — é assim
                // que o selo do Clube aparece no cabeçalho sem uma segunda
                // consulta só para ele (o cabeçalho do perfil faz igual).
                onCarregar={(d) => setClubeAtivo(!!d?.clube?.ativo)}
                onComprar={() => abrirAbaDoPerfil("creditos")}
              />
              </>
            )}

            {/* Perfil/Login */}
            {user ? (
              <Link
                to={main + "perfil"}
                onClick={scrollToTop}
                className="flex items-center gap-2.5 hover:text-insigne-orange transition-colors"
              >
                {/* Avatar: foto real do perfil, ou a marca da Insigne como fallback.
                    44px desde 11/09/2026 (pedido do dono): com 32px a foto era
                    menor que o carrinho ao lado, e é ela que diz de quem é a
                    conta. */}
                <div className="w-11 h-11 rounded-full overflow-hidden flex items-center justify-center shrink-0
                                bg-gradient-to-br from-insigne-orange to-insigne-orange-dark text-paper text-sm font-bold">
                  {fotoPerfil ? (
                    <img src={fotoPerfil} alt={primeiroNome} className="w-full h-full object-cover" />
                  ) : (
                    <MarcaPadrao />
                  )}
                </div>

                {/* ===== NOME EM CIMA, SELOS EMBAIXO =====
                    O desenho que o dono mandou: a coluna ao lado da foto. Os
                    selos são SÓ O SÍMBOLO — no perfil eles são plaquinhas
                    escritas ("Autor Insigne", "Clube Insigne"), e o texto
                    inteiro aqui empurraria a busca e o carrinho. A cor é a
                    mesma das plaquinhas (âmbar do autor, vermelho do Ticket), o
                    `title` diz o nome a quem passa o mouse e o `sr-only` diz o
                    mesmo a quem usa leitor de tela, que não vê cor nenhuma.

                    A coluna inteira é `md:flex`, como o nome já era: no celular
                    fica só a foto, que é o que cabe. */}
                <span className="hidden md:flex flex-col items-start leading-none">
                  <span className="uppercase">{primeiroNome}.</span>

                  {(ehAutor || clubeAtivo) && (
                    <span className="mt-1.5 flex items-center gap-1">
                      {ehAutor && (
                        <span
                          title="Autor Insigne"
                          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-selo
                                     bg-gradient-to-br from-[#f0a23b] to-[#d97b1a] text-ink-fixed text-[11px] shadow-md"
                        >
                          <i className="bi bi-book-half" aria-hidden="true" />
                          <span className="sr-only">Autor Insigne</span>
                        </span>
                      )}
                      {clubeAtivo && (
                        <span
                          title="Clube Insigne ativo"
                          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-selo
                                     bg-insigne-ticket text-insigne-ticket-contraste text-[11px] shadow-md"
                        >
                          <i className="bi bi-ticket-perforated-fill" aria-hidden="true" />
                          <span className="sr-only">Clube Insigne ativo</span>
                        </span>
                      )}
                    </span>
                  )}
                </span>
              </Link>
            ) : (
              <Link
                to={main + "login"}
                onClick={scrollToTop}
                className="flex items-center gap-2 hover:text-insigne-orange transition-colors"
              >
                <i className="uil uil-user text-2xl" />
                <span className="hidden sm:inline uppercase">Login</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* ===== Faixa inferior: categorias + atalhos + Publique + Promoções ===== */}
      <div className="bg-insigne-navy-router border-t border-line">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 h-[50px] md:h-[56px] flex items-center justify-between text-white text-xs md:text-[13px] uppercase">
          {/* Lado esquerdo: hambúrguer + Todas as Categorias
              Em telas md+ abre o painel CategoriasDropdown;
              em mobile (<md) ainda usa o sidebar Menu p/ navegação rápida. */}
          <button
            onClick={() => {
              if (window.matchMedia("(min-width: 768px)").matches) {
                setOpenDropdown((d) => (d === "categorias" ? null : "categorias"));
              } else {
                toggleSidebar();
              }
            }}
            className="link-sublinhado flex items-center gap-2 hover:text-insigne-orange transition-colors"
          >
            <img src={MenuBars} alt="Menu" className="w-5" />
            <span className="hidden sm:inline">Todas as categorias</span>
            <span className="hidden md:inline w-px h-5 bg-white/20 ml-2" />
          </button>

          {/* Centro: atalhos de navegação (some no mobile).
              O marcador em losango que desliza até a rota atual mora no
              NavRotas — ver o comentário de origem lá. */}
          <NavRotas links={navLinks} onNavigate={scrollToTop} />

          {/* O MESMO destino de LIVROS, para o celular. `NavRotas` é
              `hidden md:flex`, então até aqui a barra do celular não tinha
              caminho visível nenhum para o acervo: era o hambúrguer ou nada.
              Fica FORA do `NavRotas` de propósito — lá dentro ele entraria no
              cálculo do marcador em losango que desliza entre as rotas, e um
              item que aparece e some conforme a largura da tela faria o
              marcador saltar para a posição errada ao girar o aparelho. */}
          <Link
            to={main + "livros"}
            onClick={scrollToTop}
            className="md:hidden flex items-center gap-1.5 font-bold tracking-wide
                       hover:text-insigne-orange transition-colors"
          >
            <i className="bi bi-book-half text-sm" />
            Livros
          </Link>

          {/* Direita: Publique seu livro */}
          <div className="flex items-center gap-3 md:gap-4">
            <Link
              to={WHATSAPP_PUBLICAR_HREF}
              target="_blank"
              className="link-sublinhado hidden md:flex items-center gap-2 hover:text-insigne-orange transition-colors"
            >
              <i className="bi bi-tag-fill" />
              Publique seu livro!
            </Link>
          </div>
        </div>
      </div>

      {/* Sidebars laterais (menu + carrinho) */}
      <Menu
        toggleSidebar={toggleSidebar}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        toggleCarrinho={toggleCarrinho}
      />
      <Carrinho
        toggleSidebar={toggleCarrinho}
        isOpen={isCarrinhoOpen}
        setIsOpen={setCarrinhoIsOpen}
      />

      {/* Painéis (dropdowns) — controlados pelo state openDropdown */}
      <CategoriasDropdown
        open={openDropdown === "categorias"}
        onClose={() => setOpenDropdown(null)}
      />
    </header>
  );
};

export default Header;
